import React, { useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';

const ProportionalBlocks = ({
    Data,
    totalCounts,
    onDataPointClick,
    isLoading,
    containerWidth = 50,
    isFeedback,
    onReady
}) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 660 });

    const chartData = useMemo(() => {
        if (!Data || (Array.isArray(Data) ? Data.length === 0 : Object.keys(Data).length === 0)) {
            return null;
        }

        let items = [];

        if (isFeedback === true && Array.isArray(Data)) {
            items = Data
                .filter(item => {
                    const hasValidAttribute = item.attribute_value !== null &&
                        item.attribute_value !== undefined &&
                        item.attribute_value !== '';
                    const count = item.count !== undefined ? item.count : (item.value || 0);
                    const hasValidCount = count > 0;

                    return hasValidAttribute && hasValidCount;
                })
                .map((item, idx) => {
                    const attributeValue = item.attribute_value;
                    const count = item.count !== undefined ? item.count : (item.value || 0);
                    return {
                        name: String(attributeValue).replace(/_/g, ' '),
                        value: count,
                        key: attributeValue,
                        rawData: item,
                        index: idx
                    };
                });
        } else {
            items = Object.entries(Data)
                .map(([key, categoryData], idx) => ({
                    name: key.replace(/_/g, ' '),
                    value: categoryData.count || 0,
                    key: key,
                    rawData: categoryData,
                    index: idx
                }))
                .filter(item => item.value > 0);
        }

        return {
            name: "root",
            children: items
        };
    }, [Data, isFeedback]);





    // Compute size based on actual container using ResizeObserver
    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const cw = entry.contentRect.width;
                const ch = entry.contentRect.height;

                // Use actual container height if available, otherwise fallback to aspect ratio
                if (ch > 50) {
                    setDimensions({ width: cw, height: ch });
                } else {
                    const isSmall = cw < 768;
                    const aspectRatio = isSmall ? 1 : 0.75;
                    const minH = isSmall ? 360 : 460;
                    const maxH = Math.max(minH, window.innerHeight * 0.8);
                    const calcH = Math.max(minH, cw * aspectRatio);
                    setDimensions({ width: cw, height: Math.min(calcH, maxH) });
                }
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [containerWidth]);

    const width = dimensions.width;
    const height = dimensions.height;


    useEffect(() => {
        if (!chartData || !svgRef.current) return;

        d3.select(svgRef.current).selectAll("*").remove();

        const svg = d3.select(svgRef.current)
            .attr("viewBox", [0, 0, width, height])
            // .attr("preserveAspectRatio", "none")
            .style("font", "14px sans-serif");

        const tooltip = d3.select(tooltipRef.current);

        const values = chartData.children.map(d => d.value);
        const maxValue = Math.max(...values);
        const minValue = Math.min(...values);

        const root = d3.hierarchy(chartData)
            .sum(d => d.value)
            .sort((a, b) => b.value - a.value);

        d3.treemap()
            .size([width, height])
            .padding(2)
            .round(true)(root);

        const zoom = d3.zoom()
            .scaleExtent([1, 10])
            .on("zoom", (event) => {
                g.attr("transform", event.transform);
            });

        svg.call(zoom);

        const g = svg.append("g");

        const cell = g.selectAll("g")
            .data(root.leaves())
            .join("g")
            .attr("transform", d => `translate(${d.x0},${d.y0})`);

        cell.append("rect")
            .attr("width", d => d.x1 - d.x0)
            .attr("height", d => d.y1 - d.y0)
            .attr("fill", (d, i) => {
                const MAP_COLORS = [
                    // '#D0CEFB',
                    // '#FEB4AA',
                    // '#B8DFFE',
                    '#f5dad6ff',
                    '#D0CEFB',
                    '#FCE7B8',
                    '#c6e1ff',
                    // '#FCD4E3',
                    // '#c8f0e6ff',

                ];
                return MAP_COLORS[i % MAP_COLORS.length];
            })
            .attr("stroke", "white")
            .attr("stroke-width", 1)
            .style("cursor", "pointer")
            .on("mouseover", function (event, d) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("opacity", 0.85);

                tooltip
                    .style("opacity", 1)
                    .html(`
                        <div style="font-weight: bold; margin-bottom: 4px; text-transform: capitalize;">
                            ${d.data.name}
                        </div>
                        <div>Count: ${d.value.toLocaleString()}</div>
                        <div>Percentage: ${((d.value / root.value) * 100).toFixed(2)}%</div>
                    `);

                // Smart positioning based on viewport
                const tooltipNode = tooltip.node();
                const tooltipWidth = tooltipNode.offsetWidth;
                const tooltipHeight = tooltipNode.offsetHeight;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                let left = event.clientX + 10;
                let top = event.clientY - 10;
                if (left + tooltipWidth > viewportWidth) {
                    left = event.clientX - tooltipWidth - 10;
                }
                if (top + tooltipHeight > viewportHeight) {
                    top = event.clientY - tooltipHeight - 10;
                }
                if (top < 0) {
                    top = event.clientY + 10;
                }
                tooltip
                    .style("left", left + "px")
                    .style("top", top + "px");
            })
            .on("mousemove", function (event) {
                // Smart positioning based on viewport
                const tooltipNode = tooltip.node();
                const tooltipWidth = tooltipNode.offsetWidth;
                const tooltipHeight = tooltipNode.offsetHeight;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Use clientX/Y for fixed positioning (viewport-relative)
                let left = event.clientX + 10;
                let top = event.clientY - 10;

                if (left + tooltipWidth > viewportWidth) {
                    left = event.clientX - tooltipWidth - 10;
                }
                if (top + tooltipHeight > viewportHeight) {
                    top = event.clientY - tooltipHeight - 10;
                }

                if (top < 0) {
                    top = event.clientY + 10;
                }

                tooltip
                    .style("left", left + "px")
                    .style("top", top + "px");
            })
            .on("mouseout", function () {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr("opacity", 1);

                tooltip.style("opacity", 0);
            })
            .on("click", function (event, d) {
                if (onDataPointClick) {
                    onDataPointClick(null, null, {
                        dataPointIndex: d.data.index,
                        selectedCategory: d.data.key,
                        categoryData: d.data.rawData
                    });
                }
            });

        cell.append("clipPath")
            .attr("id", (d, i) => `clip-${i}`)
            .append("rect")
            .attr("width", d => d.x1 - d.x0 - 4)
            .attr("height", d => d.y1 - d.y0 - 4);

        const text = cell.append("text")
            .attr("clip-path", (d, i) => `url(#clip-${i})`)
            .style("user-select", "none")
            .style("pointer-events", "none");

        const maxArea = d3.max(root.leaves(), d => (d.x1 - d.x0) * (d.y1 - d.y0));
        const minArea = d3.min(root.leaves(), d => (d.x1 - d.x0) * (d.y1 - d.y0));

        const fontScale = d3.scaleLinear()
            .domain([minArea, maxArea])
            .range(isFeedback ? [7, 14] : [7, 14])
            .clamp(true);

        const lengthScale = d3.scaleLinear()
            .domain([minArea, maxArea])
            .range(isFeedback ? [8, 48] : [8, 56])
            .clamp(true);

        const yScale = d3.scaleLinear()
            .domain([minArea, maxArea])
            .range(isFeedback ? [12, 24] : [14, 26])
            .clamp(true);

        text.append("tspan")
            .attr("x", 6)
            .attr("y", d => {
                const area = (d.x1 - d.x0) * (d.y1 - d.y0);
                return yScale(area) + 6;
            })
            .attr("fill", "black")
            .attr("font-weight", "500")
            // .style("text-shadow", "0 1px 4px rgba(0,0,0,0.7)")
            .style("font-size", d => {
                const area = (d.x1 - d.x0) * (d.y1 - d.y0);
                const fontSize = fontScale(area);
                return `${14}px`;
            })
            .text(d => {
                const width = d.x1 - d.x0;
                const height = d.y1 - d.y0;

                if (width < 40 || height < 25) return "";

                const name = d.data.name;


                const charWidth = 9;
                const maxChars = Math.floor((width - 8) / charWidth);

                if (name.length > maxChars) {
                    if (maxChars < 3) return "";
                    return name.substring(0, maxChars - 2) + "..";
                }
                return name;
            })
            .style("text-transform", "capitalize")
            .style("font-family", "helvetica, sans-serif", "important");

        text.append("tspan")
            .attr("x", 6)
            .attr("y", d => {
                const area = (d.x1 - d.x0) * (d.y1 - d.y0);
                const fontSize = fontScale(area) + 6;
                return yScale(area) + fontSize + 6;
            })
            .attr("fill", "black")
            .attr("font-weight", "500")
            .style("font-size", d => {
                const area = (d.x1 - d.x0) * (d.y1 - d.y0);
                const fontSize = fontScale(area) * 0.75;
                return `${Math.max(10, 8)}px`;
            })
            .text(d => {
                const area = (d.x1 - d.x0) * (d.y1 - d.y0);
                const width = d.x1 - d.x0;
                const height = d.y1 - d.y0;

                if (width < 60 || height < 40 || area < maxArea * 0.03) return "";

                const percentage = ((d.value / root.value) * 100).toFixed(1);
                return `${percentage}%`;
            })
            .style("font-family", "helvetica, sans-serif", "important");

        if (typeof onReady === 'function') {
            requestAnimationFrame(() => onReady());
        }

    }, [chartData, onDataPointClick, containerWidth, width, height, onReady]);

    useEffect(() => {
        if (!chartData && !isLoading && typeof onReady === 'function') {
            onReady();
        }
    }, [chartData, isLoading, onReady]);


    if (!chartData) {
        // Show loading state if isLoading is true
        if (isLoading) {
            return (
                <div style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: window.innerWidth < 768 ? '300px' : '600px',
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem'
                    }}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 16px'
                        }}></div>
                        <p style={{
                            fontSize: '14px',
                            color: '#7f8c8d',
                            margin: 0
                        }}>
                            Loading...
                        </p>
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            );
        }

        // Show empty state only when not loading
        return (
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%', // Changed from 1000px
                minHeight: window.innerWidth < 768 ? '300px' : '600px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #e0e0e0',
                padding: '1rem'
            }}>
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    {/* <div style={{
                        fontSize: '64px',
                        marginBottom: '16px',
                        animation: 'float 3s ease-in-out infinite'
                    }}>
                        {isFeedback ? '📊' : '🎉'}
                    </div> */}
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        margin: '0 0 8px 0'
                    }}>
                        {isFeedback ? 'No Feedback Data Available' : 'No Subcategories Found'}
                    </h2>
                    <p style={{
                        fontSize: '16px',
                        color: '#7f8c8d',
                        margin: '8px 0 0 0',
                        maxWidth: '300px'
                    }}>
                        {isFeedback
                            ? 'No feedback distribution data found for the selected filters. Try adjusting your search criteria.'
                            : 'Great news! There are no subcategories matching your criteria. Everything looks good here! 😊'
                        }
                    </p>
                </div>

                <style>{`
                    @keyframes float {
                        0%, 100% {
                            transform: translateY(0px);
                        }
                        50% {
                            transform: translateY(-20px);
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
                fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem',
                color: '#666',
                marginTop: 0,
                marginBottom: '0.1rem',
                padding: '0.2rem',
                flexShrink: 0
            }}>
                Zoom in for better view. Click on blocks to filter data.
            </div>
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                    }}>
                        <div style={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    </div>
                )}


                <div ref={containerRef} style={{
                    width: '100%',
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <svg
                        ref={svgRef}
                        // preserveAspectRatio="none"
                        style={{
                            // width: '100%',
                            // height: '100%',
                            display: 'block'
                        }}
                    />
                </div>


                <div
                    ref={tooltipRef}
                    style={{
                        position: 'fixed',
                        opacity: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        fontSize: '13px',
                        zIndex: 1000,
                        transition: 'opacity 0.2s',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                />

                <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            </div>
        </div>


    );
};

export default ProportionalBlocks;